import { Component, Input, OnInit, OnDestroy, AfterViewInit, HostListener, ViewChild, ElementRef, EventEmitter, Output } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import { IOrganizationService } from 'src/app/models/muli-org/organization-service.model';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { CommonModule } from '@angular/common';
import { MatMultiSelectPaginationComponent } from '../mat-multiselect/mat-multiselect-pagination.component';

interface RowInfo {
  boxes: HTMLElement[];
  firstBox: HTMLElement;
  lastBox: HTMLElement;
  top: number;
}

@Component({
  selector: 'app-org-structure',
  templateUrl: './org-structure.component.html',
  styleUrls: ['./org-structure.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatButtonModule,
    MatMultiSelectPaginationComponent
  ]
})
export class OrgStructureComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() organizationId: string | number;
  @Input() organization: any;
  @Input() isAdmin: boolean = false;
  @Input() isSystemUser: boolean = false;
  @Input() organizationService!: IOrganizationService;
  @Output() relationshipsChanged = new EventEmitter<void>(); 

  @ViewChild('childrenContainer') childrenContainer?: ElementRef;
  @ViewChild('tJunction') tJunction?: ElementRef;

  isEditingStructure = false;
  isLoading = false;
  childOrganizations: any[] = [];
  allProviderOrganizations: any[] = [];
  parentOrganization: any = null;
  currentOrgId: number;
  horizontalLineStyle: any = {};
  boxesToHideConnectors: Set<number> = new Set();
  hasNextPageOrganizations = true;
  currentPageOrganizations = 1;
  pageSize = 20;

  structureForm: FormGroup = new FormGroup({
    childOrgIds: new FormControl([])
  });

  subscriptions: Subscription[] = [];

  constructor() {}

  ngOnInit(): void {
    this.loadOrganizationRelationships();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.calculateHorizontalLine();
    }, 200);
  }

  ngOnDestroy(): void {
    this.subscriptions?.forEach((s) => s.unsubscribe());
  }

  @HostListener('window:resize')
  onResize() {
    setTimeout(() => {
      this.calculateHorizontalLine();
    }, 100);
  }

  loadOrganizationRelationships() {
    const orgId = this.organizationId.toString();
    const sub = this.organizationService
      .getRelationships(orgId)
      .subscribe((response) => {
        if (!response.isError) {
          this.parentOrganization = response.data.parent;
          this.childOrganizations = response.data.children || [];
          this.currentOrgId = parseInt(orgId);

          setTimeout(() => {
            this.calculateHorizontalLine();
          }, 100);
        }
      });
    this.subscriptions.push(sub);
  }

toggleEditStructure() {
  if (!this.isEditingStructure) {
    this.currentPageOrganizations = 1;
    this.hasNextPageOrganizations = true;
    this.allProviderOrganizations = [];
    this.fetchOrganizations({ searchKey: '', reset: true });
  }
  this.isEditingStructure = !this.isEditingStructure;
}

  onLoadMoreOrganizations(event: { searchKey: string; reset?: boolean }) {
    this.fetchOrganizations(event);
  }

private fetchOrganizations(event: { searchKey: string; reset?: boolean }) {
  const { searchKey, reset } = event;

  const params: any = {
    sortColumn: 'name',
    sortOrder: 'asc',
    page: reset ? 1 : this.currentPageOrganizations + 1,
    pageSize: this.pageSize,
    searchKey: searchKey || '',
    isActive: true 
  };
  const sub = this.organizationService
    .get(params)
    .subscribe((response: any) => {      
      if (!response.isError && response.data?.items) {        
        const newOrgs = response.data.items
          .filter((org: any) => org.id !== parseInt(this.organizationId.toString()))
          .map((org: any) => ({ id: org.id, name: org.name }));
        if (reset) {
          this.allProviderOrganizations = newOrgs;
          this.currentPageOrganizations = 1;
          const childrenNotInList = this.childOrganizations.filter((child: any) => 
            !this.allProviderOrganizations.some(org => org.id === child.id)
          );
          
          if (childrenNotInList.length > 0) {
            const childrenToAdd = childrenNotInList.map((child: any) => ({
              id: child.id,
              name: child.name
            }));
            this.allProviderOrganizations = [
              ...childrenToAdd,
              ...this.allProviderOrganizations
            ];
          }
                    const selectedChildren = this.allProviderOrganizations.filter(org => {
            return this.childOrganizations.some((child: any) => child.id === org.id);
          });
          this.structureForm.patchValue({
            childOrgIds: selectedChildren
          });
        } else {
          this.allProviderOrganizations = [
            ...this.allProviderOrganizations,
            ...newOrgs
          ];
          this.currentPageOrganizations = response.data.page;
        }
        this.hasNextPageOrganizations = response.data.hasNextPage;
      } else {
        console.error('Response error or no items');
      }
    });

  this.subscriptions.push(sub);
}

  cancelEditStructure() {
    this.isEditingStructure = false;
    this.structureForm.reset();
  }

  saveStructureChanges() {
    this.isLoading = true;
    const orgId = this.organizationId.toString();

    const currentChildIds = this.childOrganizations.map(c => c.id);
    const selectedIds = this.structureForm.value.childOrgIds.map((c: any) => c.id);

    const toLink = selectedIds.filter((id: number) => !currentChildIds.includes(id));
    const toUnlink = currentChildIds.filter((id: number) => !selectedIds.includes(id));
    const operations: Observable<any>[] = [];

    if (toLink.length > 0) {
      operations.push(
        this.organizationService.linkParentChild(orgId, toLink)
      );
    }
    toUnlink.forEach((childId: number) => {
      operations.push(
        this.organizationService.unlinkParentChild(orgId, childId.toString())
      );
    });

    if (operations.length === 0) {
      this.isLoading = false;
      this.cancelEditStructure();
      return;
    }

    const executeOperations = (index: number) => {
      if (index >= operations.length) {
        this.isLoading = false;
        this.organizationService.clearRelationshipsCache(orgId);
        this.loadOrganizationRelationships();
        this.cancelEditStructure();
        this.relationshipsChanged.emit();
        return;
      }

      const sub = operations[index].subscribe({
        next: () => {
          executeOperations(index + 1);
        },
        error: (error) => {
          this.isLoading = false;
          console.error('Error updating relationships:', error);
          this.loadOrganizationRelationships();
          this.cancelEditStructure();
        }
      });
      this.subscriptions.push(sub);
    };

    executeOperations(0);
  }

  calculateHorizontalLine(): void {
    if (this.childOrganizations.length > 0) {
      this.calculateLineForContainer();
    }
  }

  private calculateLineForContainer(): void {
    const container = document.querySelector('.org-level-children') as HTMLElement;

    if (!container) {
      this.horizontalLineStyle = {};
      this.boxesToHideConnectors.clear();
      return;
    }

    const children = container.querySelectorAll('.org-child-wrapper') as NodeListOf<HTMLElement>;

    if (children.length === 0) {
      this.horizontalLineStyle = {};
      this.boxesToHideConnectors.clear();
      return;
    }

    const orgChart = container.closest('.org-chart-container') as HTMLElement;
    if (!orgChart) {
      this.horizontalLineStyle = {};
      this.boxesToHideConnectors.clear();
      return;
    }

    const rows = this.groupBoxesByRow(Array.from(children));

    if (rows.length === 0) {
      this.horizontalLineStyle = {};
      this.boxesToHideConnectors.clear();
      return;
    }

    const tJunction = orgChart.querySelector('.flowchart-t-junction') as HTMLElement;
    if (!tJunction) {
      this.horizontalLineStyle = {};
      return;
    }

    const tJunctionRect = tJunction.getBoundingClientRect();
    const firstRow = rows[0];
    const firstRect = firstRow.firstBox.getBoundingClientRect();
    const lastRect = firstRow.lastBox.getBoundingClientRect();

    const firstCenter = (firstRect.left + firstRect.width / 2) - tJunctionRect.left;
    const lastCenter = (lastRect.left + lastRect.width / 2) - tJunctionRect.left;

    this.horizontalLineStyle = {
      left: (firstCenter) + 'px',
      width: (lastCenter - firstCenter) + 'px'
    };

    this.boxesToHideConnectors.clear();
    const childWrappers = Array.from(children);

    for (let i = 1; i < rows.length; i++) {
      const currentRow = rows[i];
      currentRow.boxes.forEach(box => {
        const boxIndex = childWrappers.findIndex(wrapper => wrapper === box);
        if (boxIndex !== -1) {
          this.boxesToHideConnectors.add(boxIndex);
        }
      });
    }
  }

  private groupBoxesByRow(boxes: HTMLElement[]): RowInfo[] {
    if (boxes.length === 0) return [];

    const rows: RowInfo[] = [];
    const tolerance = 15;

    boxes.forEach(box => {
      const rect = box.getBoundingClientRect();
      const top = rect.top;

      let foundRow = rows.find(row => Math.abs(row.top - top) < tolerance);

      if (foundRow) {
        foundRow.boxes.push(box);
        const boxLeft = rect.left;
        const firstLeft = foundRow.firstBox.getBoundingClientRect().left;
        const lastLeft = foundRow.lastBox.getBoundingClientRect().left;

        if (boxLeft < firstLeft) {
          foundRow.firstBox = box;
        }
        if (boxLeft > lastLeft) {
          foundRow.lastBox = box;
        }
      } else {
        rows.push({
          boxes: [box],
          firstBox: box,
          lastBox: box,
          top: top
        });
      }
    });

    rows.sort((a, b) => a.top - b.top);

    return rows;
  }
}